import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginApi, logoutApi, getMeApi, updateProfileApi, changePasswordApi } from '../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const res = await getMeApi();
      if (res && res.user) {
        // Ensure user data includes role and permissions
        const userData = {
          ...res.user,
          role_name: res.user.role_name || res.user.role || 'User',
          permissions: res.user.permissions || []
        };
        setUser(userData);
        setIsAuthenticated(true);
        
        // Update localStorage with fresh user data
        localStorage.setItem('ransilu_user_data', JSON.stringify(userData));
      } else {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('ransilu_user_data');
      }
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('ransilu_user_data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (loginCredential, password, rememberMe = false) => {
    const res = await loginApi({ login: loginCredential, password, rememberMe });
    if (res && res.user) {
      // Store user with complete role and permission data
      const userData = {
        ...res.user,
        role_name: res.user.role_name || res.user.role || 'User',
        permissions: res.user.permissions || []
      };
      setUser(userData);
      setIsAuthenticated(true);
      
      // Store user data in localStorage for offline access
      localStorage.setItem('ransilu_user_data', JSON.stringify(userData));
      return res;
    } else {
      throw new Error(res.message || 'Login failed.');
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await logoutApi();
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('ransilu_user_data');
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    const res = await updateProfileApi(profileData);
    if (res && res.user) {
      const userData = {
        ...res.user,
        role_name: res.user.role_name || res.user.role || 'User',
        permissions: res.user.permissions || []
      };
      setUser(userData);
      localStorage.setItem('ransilu_user_data', JSON.stringify(userData));
    }
    return res;
  };

  const changePassword = async (passwordData) => {
    const res = await changePasswordApi(passwordData);
    if (res && res.user) {
      const userData = {
        ...res.user,
        role_name: res.user.role_name || res.user.role || 'User',
        permissions: res.user.permissions || []
      };
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('ransilu_user_data', JSON.stringify(userData));
    }
    return res;
  };

  const hasPermission = (tabName, action) => {
    if (!user) return false;
    
    if (!user.permissions || !Array.isArray(user.permissions)) return false;

    const normalizeAction = (a) => {
      if (!a) return '';
      const lower = a.toLowerCase();
      if (lower === 'view') return 'read';
      if (lower === 'update') return 'edit';
      return lower;
    };

    const normalizeModule = (m) => {
      if (!m) return '';
      return m.toLowerCase()
        .replace(/\s*&\s*/g, '_')
        .replace(/-/g, '')
        .replace(/\s+/g, '_');
    };

    const nTab = tabName ? normalizeModule(tabName) : null;
    const nAction = action ? normalizeAction(action) : null;

    return user.permissions.some(p => {
      // Match by permission_name string e.g. 'songs:view'
      const permName = (p.permission_name || '').toLowerCase();
      if (permName) {
        const [pMod, pAct] = permName.split(':');
        if (nTab && nAction) {
          if (normalizeModule(pMod) === nTab && normalizeAction(pAct) === nAction) return true;
        } else if (nTab) {
          if (normalizeModule(pMod) === nTab) return true;
        }
      }

      // Match by tab_name + action DB columns
      const pTabNorm = normalizeModule(p.tab_name || '');
      const pActionNorm = normalizeAction(p.action || '');
      if (nTab && nAction) {
        if (pTabNorm === nTab && pActionNorm === nAction) return true;
      } else if (nTab) {
        if (pTabNorm === nTab) return true;
      }

      return false;
    });
  };


  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateProfile,
        changePassword,
        hasPermission,
        refreshUser: checkAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
